import {
    MouseEvent,
    useState,
    useEffect,
    CSSProperties,
    useContext,
} from "react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { useHistory } from "react-router";
import { CurrentBrand, OrganizationStatus } from "modules/Types";
import ProfileIcon from "assets/images/profileIcon.svg";
import CustomAvatar from "./CustomAvatar";
import SearchSelector from "./AdminSearchSelectorModal";
import { Search } from "@material-ui/icons";
import useOrganization from 'Hooks/useOrganization';
import Sty, { Container, StyledLabelSpan } from "modules/Sty";
import useBrand, { checkTier } from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import { clsx, deepCopy, makeKey } from "modules/Utils";
import ButtonSymphony from "elements/ButtonSymphony";
import { MAX_BRANDS_BEFORE_TEAM_PLAN_UPGRADE_REQUIRED, PlanType, USER_PERMISSION_ON_BRAND_INFO } from "modules/Const";
import { BrandTierType } from "elements/BrandTier";
import BrandTier from "elements/BrandTier";
import Colors from "modules/Colors";
import Icon from "elements/Icon";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import useAppView from "Hooks/useAppView";
import AdminId from "elements/AdminId";
import FeatureGate from "components/FeatureGate";
import { FeatureKeys } from "features/definitions";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";

const sortAlphabetically = (brands: CurrentBrand[]) => {
    const sortedBrands = deepCopy(brands);
    return (
        sortedBrands.sort(
            (a: CurrentBrand, b: CurrentBrand) =>
                a.name
                    ?.toLowerCase()
                    .localeCompare(b.name?.toLowerCase() as string) as number
        )
    );
}

interface BrandDropdownProps {
    className: string;
    smallSidebar?: boolean;
}

const BrandDropdown = ({
    className,
    smallSidebar,
}: BrandDropdownProps) => {
    const history = useHistory();
    const { isSymphonyAdmin } = useUser()
    const {
        currentBrand,
        loadedBrands,
        setCurrentBrand,
        getBrand,
        getBrandImage,
        resetDifferentCurrentBrand
    } = useBrand();
    const { setAppView } = useAppView();

    const {
        organization,
        organizationBrandIds,
        // brandIdIsInOrganization
        // } = useOrganization()
    } = useOrganizationV2()
    const { isTeamView } = useAppView();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    const opacity = currentBrand || organization ? 1 : 0;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [brandImage, setBrandImage] = useState<string | null>(getBrandImage() || null);
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false)

    useEffect(() => {
        if (currentBrand) {
            setBrandImage(getBrandImage())
        }
    }, [currentBrand])

    const handleOpenDropdown = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }

    const handleCloseDropdown = () => {
        setAnchorEl(null)
    }

    const handleClickAddArtist = () => {
        // TODO: Revie what happens when you click this and have a team account
        const brandCount = loadedBrands?.length || 0;
        
        if (brandCount >= MAX_BRANDS_BEFORE_TEAM_PLAN_UPGRADE_REQUIRED) {
            handleOpenModal({
                source: 'brand_dropdown_add_brand',
                tab: 'team',
                upgradeCallToAction: 'Add more than 2 profiles with a Team Plan',
                highlightedPlan: PlanType.TEAM_V2,
                onlyShowPlans: [PlanType.TEAM_V2]
            });
        } else {
            history.push('/add-artist');
        }
    }

    const handleClickBrandFromDropdown = async (brand: CurrentBrand) => {
        // NOTE: this executes when you click on a brand (artist) in the dropdown
        handleCloseDropdown();
        const isDifferentBrand = brand.id !== currentBrand?.id;
        
        try {
            if (isDifferentBrand) {
                resetDifferentCurrentBrand()
            }

            // Explicitly switch to brand view first to avoid navbar flickering
            setAppView('brand', history);
            
            const updatedBrandData = await getBrand(brand.id)
            setCurrentBrand(updatedBrandData);
            
        } catch (error) {
            console.error('Error updating brand:', error);
        }
    };

    const handleClickTeamHome = () => {
        // Switch to team home view
        handleCloseDropdown();
        
        // Explicitly switch to team view
        setAppView('team', history);
        
        // Clear the current brand and navigate to team dashboard
        if (currentBrand) {
            resetDifferentCurrentBrand();
            setCurrentBrand(undefined);
        }
    }

    const renderBrandDropdownTitle = () => {
        // In team view, always show organization name regardless of currentBrand
        if (isTeamView() && organization) {
            return organization.name;
        }
        
        // In brand view, show current brand if available
        if (currentBrand) {
            return currentBrand.name;
        }
        
        return '';
    }

    const renderBrandDropdownSubtitle = () => {
        // In team view, always show team dashboard subtitle regardless of currentBrand
        if (organization && isTeamView()) {
            return (
                <div style={{ ...Sty.text12, color: Colors.greyNavbar }}>
                    Team Dashboard
                </div>
            );
        }
        
        // In brand view, show brand tier and ID if available
        if (currentBrand) {
            return (
                <>
                    <BrandTier
                        className="brandTierProfileDropdown"
                        tier={currentBrand?.tier as PlanType}
                        type={BrandTierType.TEXT}
                    />
                    <AdminId
                        className="adminIdProfileDropdown2"
                        id={currentBrand?.id}
                    />
                </>
            );
        }
        
        return null;
    }

    const renderBrandDropdownAvatar = () => {
        // In team view, always show organization avatar regardless of currentBrand
        if (isTeamView() && organization) {
            return (
                <CustomAvatar
                    className="avatarUserStatusProfileDropdown"
                    image={organization.profile_picture}
                    isTeamAvatar={true}
                />
            );
        }
        
        // In brand view, show current brand avatar if available
        if (currentBrand) {
            const image = brandImage ? brandImage : ProfileIcon;
            const isBorder = brandImage ? false : true;
            
            return (
                <CustomAvatar
                    className="avatarUserStatusProfileDropdown"
                    image={image}
                    isBorder={isBorder}
                    brandOverride={currentBrand}
                />
            );
        }
        
        // Fallback to organization or default
        if (organization) {
            return (
                <CustomAvatar
                    className="avatarUserStatusProfileDropdown"
                    image={organization.profile_picture}
                    isTeamAvatar={true}
                />
            );
        }
        
        // Final fallback to ProfileIcon
        return (
            <CustomAvatar
                className="avatarUserStatusProfileDropdown"
                image={ProfileIcon}
                isBorder={false}
            />
        );
    }

    const renderBrandsList = () => {
        // Show dropdown if there's an organization (for Team Home) or if there are brands
        if ((!loadedBrands || loadedBrands.length === 0) && !organization) {
            return null
        }
        const sortedBrands = loadedBrands && loadedBrands.length > 0 ? sortAlphabetically(loadedBrands).map((currentBrandLocal: CurrentBrand, index: number) => {
            const { name, image, permissionLevel } = currentBrandLocal;
            const tier = checkTier(currentBrandLocal)

            const isBrandInOrganization = organizationBrandIds?.includes(currentBrandLocal.id)
            // For brands from multiple organizations, check if this brand has organization info
            // Note: currentBrandLocal may have organization data from the enhanced getBrands API
            const brandOrganization = currentBrandLocal.organization || organization

            return (
                <div
                    key={makeKey(name, index)}
                    className="menuItemProfileDropdown"
                    style={sty.menuItemProfileDropdown}
                    onClick={() => handleClickBrandFromDropdown(currentBrandLocal)}
                >
                    <CustomAvatar
                        className="fromBrandDropdownCustomAvatar"
                        image={image}
                        isBorder={image ? false : true}
                        brandOverride={currentBrandLocal}
                    />
                    <div className="brandDropdownMenuItemsContent">
                        <div
                            className="brandNameProfileDropdown"
                            style={sty.brandNameProfileDropdown}
                        >
                            {name}
                        </div>
                        {brandOrganization && brandOrganization.setup_status === OrganizationStatus.READY && isBrandInOrganization &&
                            <>
                                <div
                                    className="organizationNameProfileDropdown"
                                    style={{
                                        ...Sty.text12,
                                        color: Colors.goldDark,
                                    }}
                                >
                                    {brandOrganization?.name}
                                </div>
                                <div
                                    className="permissionLevelProfileDropdown"
                                    style={{
                                        ...Sty.text12,
                                        color: permissionLevel !== 'none' ? USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.color : Colors.greyDark,
                                    }}
                                >
                                    {permissionLevel !== 'none' ? USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.title : 'No Permission'}
                                </div>
                            </>
                        }
                        <AdminId
                            className="adminIdProfileDropdown1"
                            id={currentBrandLocal.id}
                        />
                    </div>
                    <BrandTier
                        className="brandTierProfileDropdown1"
                        style={{ marginLeft: 'auto' }}
                        tier={tier}
                        type={BrandTierType.TEXT}
                        iconSize={14}
                        textSize={12}
                        isWhiteBackground
                    />
                </div>
            );
        }) : [];

        return (
            <div className="brandDropdownMenuItems" style={{ minWidth: 280 }}>
                {isSymphonyAdmin &&
                    <MenuItem
                        key="admin-search"
                        className="adminSearchBrandProfile"
                        style={sty.menuItemProfileDropdown}
                        onClick={() => {
                            setOpenAdminSelector(true)
                            handleCloseDropdown()
                        }}
                    >
                        <Container>
                            <Search />
                        </Container>
                        <Container display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                            <StyledLabelSpan
                                padding="0px 0px 0px 16px"
                                fontSize={16}
                                fontWeight={400}
                            >
                                Open Admin Search
                            </StyledLabelSpan>
                            {isSymphonyAdmin &&
                                <StyledLabelSpan
                                    paddingLeft="16px"
                                    color="#E08507"
                                    fontSize={12}
                                    fontWeight={400}
                                >
                                    Search for any artist as an admin.
                                </StyledLabelSpan>
                            }
                        </Container>
                    </MenuItem>
                }
                {organization &&
                    <div
                        key="team-home"
                        className="menuItemProfileDropdown"
                        style={{
                            ...sty.menuItemProfileDropdown,
                            backgroundColor: !currentBrand ? 'rgba(0,0,0,0.1)' : 'transparent'
                        }}
                        onClick={handleClickTeamHome}
                    >
                        <CustomAvatar
                            className="fromBrandDropdownCustomAvatar"
                            image={organization.profile_picture}
                            isTeamAvatar={true}
                        />
                        <div className="brandDropdownMenuItemsContent">
                            <div
                                className="brandNameProfileDropdown"
                                style={sty.brandNameProfileDropdown}
                            >
                                Team Home
                            </div>
                            <div
                                className="organizationNameProfileDropdown"
                                style={{
                                    ...Sty.text12,
                                    color: Colors.goldDark,
                                }}
                            >
                                {organization.name}
                            </div>
                        </div>
                        {!currentBrand &&
                            <Icon
                                className="selectedIndicator"
                                icon="check"
                                color={Colors.green}
                                size={16}
                                style={{ marginLeft: 'auto' }}
                            />
                        }
                    </div>
                }
                {sortedBrands}
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <style>
                {`
                    .mainAddNewBrandButton, .menuItemProfileDropdown {
                        &:hover {
                        background-color: rgba(0,0,0,0.05) !important;
                        }
                    }
                `}
            </style>

            {/* NOTE: this is the Dropdown Button */}
            <div
                className={clsx('brandDropdownComponent', className)}
                style={{
                    cursor: 'pointer',
                    opacity: opacity,
                    borderRadius: 16,
                    paddingTop: 4,
                    paddingBottom: 4,
                }}
            >
                <ButtonSymphony
                    className="brandDropdownButton"
                    style={sty.brandDropdownButton}
                    styleButtonContent={{
                        width: '100%',
                    }}
                    onClick={handleOpenDropdown} // NOTE: this opens brandDropdownMenu
                >
                    {renderBrandDropdownAvatar()}
                    <div style={sty.brandDropdownTextContainer}>
                        <div 
                            className="brandDropdownTitle"
                            style={{
                                ...Sty.text16white,
                                ...sty.brandDropdownTitle
                            }}
                        >
                            {renderBrandDropdownTitle()}
                        </div>
                        <div className="brandDropdownTierContainer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {renderBrandDropdownSubtitle()}
                        </div>
                    </div>
                    <Icon
                        className="brandDropdownIcon"
                        style={{ opacity: 0.5, marginLeft: 'auto' }}
                        icon="chevronDown"
                        color={Colors.white}
                        size={16}
                    />
                </ButtonSymphony>

                {/* NOTE: this is the Menu pop up */}
                <Menu
                    id="simple-menu"
                    className="brandDropdownMenu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleCloseDropdown}
                    MenuListProps={{
                        style: {
                            padding: 0,
                            maxWidth: 400
                        },
                    }}
                >
                    {(loadedBrands || organization) &&
                        renderBrandsList()
                    }
                    {/* <FeatureGate
                        feature={FeatureKeys.TEAM_MULTI_BRAND}
                        upgradeCallToAction="Upgrade to Teams to manage more than 3 profiles"
                        upgradeSource="Brand Dropdown - Add New Brand"
                        fallback={
                            loadedBrands && loadedBrands.length >= 3 ? (
                                <ButtonSymphony
                                    key="add-new-brand-disabled"
                                    className="mainAddNewBrandButton"
                                    style={sty.mainAddNewBrandButton}
                                    onClick={() => { }}
                                    variant="text"
                                    disabled
                                >
                                    + Add New Brand (Upgrade Required)
                                </ButtonSymphony>
                            ) : null
                        }
                        showUpgrade={false}
                    > */}
                    <ButtonSymphony
                        key="add-new-brand"
                        className="mainAddNewBrandButton"
                        style={sty.mainAddNewBrandButton}
                        onClick={handleClickAddArtist}
                        variant="text"
                    >
                        + Add New Brand
                    </ButtonSymphony>
                    {/* </FeatureGate> */}
                </Menu>
                {isSymphonyAdmin &&
                    <SearchSelector
                        className="fromBrandDropdownSearchSelector"
                        mode="default"
                        open={openAdminSelector}
                        onClose={() => {
                            setOpenAdminSelector(false)
                            handleCloseDropdown()
                        }}
                    />
                }
            </div>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    mainAddNewBrandButton: {
        display: 'flex',
        width: '100%',
        paddingTop: 18,
        paddingBottom: 16,
        borderRadius: 0,
    },
    brandDropdownButton: {
        backgroundColor: 'transparent',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 6,
        paddingBottom: 6,
        height: 'auto',
        textAlign: 'left',
        justifyContent: 'flex-start',
        width: '100%',
    },
    brandDropdownTextContainer: {
        flex: 1,
        minWidth: 0, // Allows flex item to shrink below content size
    },
    brandDropdownTitle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    brandDropdownUserNameText: {
        color: "white",
        fontSize: 16,
        textAlign: "left",
        padding: "0 4px 0 4px",
        width: "120px",
        height: "56px"
    },
    menuItemProfileDropdown: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        cursor: 'pointer',
        minHeight: 67,
        borderBottom: Sty.border,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 16,
        paddingRight: 16,
    },
    brandNameProfileDropdown: {
        fontSize: 16,
        fontWeight: 400,
        width: '100%',
    },
}

export default BrandDropdown;
